import { useState, useRef } from 'react'
import { Receipt, Plus, X, Send, Lock, CreditCard, Trash2, Paperclip, ExternalLink, RefreshCw, Eye, EyeOff } from 'lucide-react'
import clsx from 'clsx'
import toast from 'react-hot-toast'
import { useAuth } from '../context/AuthContext'
import {
  useInvoices, useInvoice, useAddInvoice,
  useAddInvoiceItem, useUpdateInvoiceItem, useDeleteInvoiceItem,
  useSendInvoice, useFinaliseInvoice, useMarkInvoicePaid,
  useVehicles, usePopulateInvoiceFromJob, useUpdateInvoice,
  useAddInvoiceReceipt, useDeleteDocument,
} from '../api/hooks'

// ─── Helpers ──────────────────────────────────────────────────────────────────

const inputCls = `w-full px-3 py-2 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] placeholder-[var(--text-muted)]
  focus:outline-none focus:border-indigo-500 transition-colors`

const STATUS = {
  draft:     { label: 'Draft',     cls: 'bg-gray-500/15 text-gray-400' },
  sent:      { label: 'Sent',      cls: 'bg-blue-500/15 text-blue-400' },
  paid:      { label: 'Paid',      cls: 'bg-green-500/15 text-green-400' },
  overdue:   { label: 'Overdue',   cls: 'bg-red-500/15 text-red-400' },
  cancelled: { label: 'Cancelled', cls: 'bg-gray-500/15 text-gray-400' },
}

const PAYMENT_METHODS = ['Bank transfer', 'Cash', 'Card', 'Cheque', 'Other']

const fmtGBP  = (v) => `£${Number(v || 0).toFixed(2)}`
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB') : '—'

// ─── Status badge ─────────────────────────────────────────────────────────────

function StatusBadge({ status, finalised }) {
  const s = STATUS[status] || { label: status, cls: 'bg-gray-500/15 text-gray-400' }
  return (
    <div className="flex items-center gap-1.5">
      <span className={clsx('px-2 py-0.5 text-xs font-medium rounded-full', s.cls)}>{s.label}</span>
      {finalised && (
        <span className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium rounded-full bg-indigo-500/15 text-indigo-400">
          <Lock size={10} />
          Finalised
        </span>
      )}
    </div>
  )
}

// ─── Create Invoice modal ─────────────────────────────────────────────────────

function CreateInvoiceModal({ onClose }) {
  const addInvoice = useAddInvoice()
  const { data: vehicles = [], isLoading: vLoading } = useVehicles()

  const [form, setForm] = useState({ vehicleId: '', dueDate: '', notes: '' })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!form.vehicleId) return toast.error('Please select a vehicle')
    try {
      await addInvoice.mutateAsync({
        vehicleId: form.vehicleId,
        dueDate:   form.dueDate || null,
        notes:     form.notes   || null,
      })
      toast.success('Invoice created')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create invoice')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-md bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">New Invoice</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Vehicle</label>
            <select value={form.vehicleId} onChange={set('vehicleId')} className={inputCls} required>
              <option value="">
                {vLoading ? 'Loading…' : vehicles.length ? 'Select vehicle' : 'No vehicles linked'}
              </option>
              {vehicles.map(v => (
                <option key={v.id} value={v.id}>{v.registration} — {v.make} {v.model}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Due date</label>
            <input type="date" value={form.dueDate} onChange={set('dueDate')} className={inputCls} />
          </div>
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Notes</label>
            <textarea rows={2} value={form.notes} onChange={set('notes')}
              className={clsx(inputCls, 'resize-none')} />
          </div>
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={addInvoice.isPending}
              className="flex-1 py-2.5 bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {addInvoice.isPending ? 'Creating…' : 'Create Invoice'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Add item form ────────────────────────────────────────────────────────────

function AddItemForm({ invoiceId }) {
  const addItem = useAddInvoiceItem()
  const [form, setForm] = useState({
    description: '', type: 'labour', quantity: '1', unitPrice: '0.00', vatRate: '20',
  })
  const set = f => e => setForm(p => ({ ...p, [f]: e.target.value }))

  const handleAdd = async (e) => {
    e.preventDefault()
    if (!form.description.trim()) return toast.error('Description required')
    try {
      await addItem.mutateAsync({
        invoiceId,
        description: form.description,
        type:        form.type,
        quantity:    parseFloat(form.quantity)  || 1,
        unitPrice:   parseFloat(form.unitPrice) || 0,
        vatRate:     parseFloat(form.vatRate)   || 20,
      })
      setForm({ description: '', type: 'labour', quantity: '1', unitPrice: '0.00', vatRate: '20' })
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to add item')
    }
  }

  return (
    <form onSubmit={handleAdd} className="mt-3 p-3 bg-[var(--bg-base)] border border-[var(--border)] rounded-lg space-y-2">
      <p className="text-xs font-medium text-[var(--text-secondary)]">Add line item</p>
      <div className="grid grid-cols-12 gap-2">
        <input className={clsx(inputCls, 'col-span-5')} placeholder="Description"
          value={form.description} onChange={set('description')} required />
        <select value={form.type} onChange={set('type')}
          className={clsx(inputCls, 'col-span-2 cursor-pointer')}>
          <option value="labour">Labour</option>
          <option value="part">Part</option>
          <option value="misc">Misc</option>
        </select>
        <input type="number" min="0" step="0.01" placeholder="Qty"
          className={clsx(inputCls, 'col-span-2')} value={form.quantity} onChange={set('quantity')} />
        <input type="number" min="0" step="0.01" placeholder="Unit £"
          className={clsx(inputCls, 'col-span-2')} value={form.unitPrice} onChange={set('unitPrice')} />
        <button type="submit" disabled={addItem.isPending}
          className="col-span-1 flex items-center justify-center bg-indigo-600 hover:bg-indigo-700
                     disabled:opacity-50 text-white rounded-lg transition-colors">
          <Plus size={16} />
        </button>
      </div>
      <div className="flex items-center gap-2">
        <label className="text-xs text-[var(--text-muted)]">VAT %</label>
        <input type="number" min="0" step="0.1" value={form.vatRate} onChange={set('vatRate')}
          className={clsx(inputCls, 'w-20')} />
      </div>
    </form>
  )
}

// ─── Mark Paid modal ──────────────────────────────────────────────────────────

function MarkPaidModal({ invoice, onClose }) {
  const markPaid = useMarkInvoicePaid()
  const [method, setMethod] = useState('Bank transfer')

  const handleSubmit = async (e) => {
    e.preventDefault()
    try {
      await markPaid.mutateAsync({ id: invoice.id, paymentMethod: method })
      toast.success('Invoice marked as paid')
      onClose()
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to mark paid')
    }
  }

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-sm bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)]">
          <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">Mark as Paid</h2>
          <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)]">
            <X size={18} />
          </button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-xs font-medium text-[var(--text-secondary)] mb-1.5">Payment method</label>
            <select value={method} onChange={e => setMethod(e.target.value)} className={inputCls}>
              {PAYMENT_METHODS.map(m => <option key={m} value={m}>{m}</option>)}
            </select>
          </div>
          <div className="flex gap-3 pt-1">
            <button type="button" onClick={onClose}
              className="flex-1 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                         text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors">
              Cancel
            </button>
            <button type="submit" disabled={markPaid.isPending}
              className="flex-1 py-2.5 bg-green-600 hover:bg-green-700 disabled:opacity-50
                         text-white text-sm font-medium rounded-lg transition-colors">
              {markPaid.isPending ? 'Saving…' : 'Confirm Payment'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ─── Invoice detail modal ─────────────────────────────────────────────────────

function InvoiceDetailModal({ invoiceId, canAdmin, onClose }) {
  const { data: invoice, isLoading } = useInvoice(invoiceId)
  const deleteItem    = useDeleteInvoiceItem()
  const updateItem    = useUpdateInvoiceItem()
  const sendInvoice   = useSendInvoice()
  const finalise      = useFinaliseInvoice()
  const updateInvoice = useUpdateInvoice()
  const populate      = usePopulateInvoiceFromJob()
  const addReceipt    = useAddInvoiceReceipt()
  const deleteDoc     = useDeleteDocument()
  const fileRef       = useRef(null)
  const [showPaid, setShowPaid] = useState(false)

  if (isLoading) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60">
        <div className="text-[var(--text-secondary)] text-sm">Loading…</div>
      </div>
    )
  }
  if (!invoice) return null

  const editable    = !invoice.is_finalised && ['draft', 'sent'].includes(invoice.status)
  const canSend     = canAdmin && invoice.status === 'draft' && !invoice.is_finalised
  const canFinalise = canAdmin && !invoice.is_finalised && invoice.status !== 'draft'
  const canMarkPaid = canAdmin && ['sent', 'overdue'].includes(invoice.status)
  const items       = invoice.items || []
  const receipts    = invoice.receipts || []

  const prePaidTotal   = items.filter(i => i.already_paid).reduce((s, i) => s + Number(i.total), 0)
  const hasPrePaid     = prePaidTotal > 0

  const handleSend = async () => {
    if (!window.confirm('Send this invoice to the customer?')) return
    try {
      await sendInvoice.mutateAsync(invoice.id)
      toast.success('Invoice sent')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send')
    }
  }

  const handleFinalise = async () => {
    if (!window.confirm('Finalise this invoice? It will become immutable.')) return
    try {
      await finalise.mutateAsync(invoice.id)
      toast.success('Invoice finalised')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to finalise')
    }
  }

  const handleDeleteItem = async (itemId) => {
    try {
      await deleteItem.mutateAsync({ invoiceId: invoice.id, itemId })
      toast.success('Item removed')
    } catch {
      toast.error('Failed to remove item')
    }
  }

  const handleToggleAlreadyPaid = async (item) => {
    try {
      await updateItem.mutateAsync({ invoiceId: invoice.id, itemId: item.id, alreadyPaid: !item.already_paid })
    } catch {
      toast.error('Failed to update item')
    }
  }

  const handlePopulate = async () => {
    if (!window.confirm('Add items from the linked job? This will append to any existing items.')) return
    try {
      await populate.mutateAsync(invoice.id)
      toast.success('Items added from job')
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to populate')
    }
  }

  const handleToggleReceipts = async () => {
    try {
      await updateInvoice.mutateAsync({ id: invoice.id, includeReceipts: !invoice.include_receipts })
    } catch {
      toast.error('Failed to update')
    }
  }

  const handleReceiptUpload = async (e) => {
    const file = e.target.files?.[0]
    if (!file) return
    try {
      await addReceipt.mutateAsync({ invoiceId: invoice.id, file })
      toast.success('Receipt uploaded')
    } catch {
      toast.error('Upload failed')
    }
    e.target.value = ''
  }

  const handleDeleteReceipt = async (docId) => {
    if (!window.confirm('Remove this receipt?')) return
    try {
      await deleteDoc.mutateAsync(docId)
      toast.success('Receipt removed')
    } catch {
      toast.error('Remove failed')
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
      <div className="w-full max-w-2xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl shadow-xl animate-scale-in flex flex-col max-h-[90dvh]">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-[var(--border)] shrink-0">
          <div className="flex items-center gap-3 flex-wrap">
            <h2 className="font-display text-lg font-bold text-[var(--text-primary)]">
              {invoice.invoice_number}
            </h2>
            <StatusBadge status={invoice.status} finalised={invoice.is_finalised} />
          </div>
          <div className="flex items-center gap-2">
            {/* Include receipts toggle — garage admin only */}
            {canAdmin && (
              <button
                onClick={handleToggleReceipts}
                disabled={updateInvoice.isPending}
                title={invoice.include_receipts ? 'Receipts shown to customer — click to hide' : 'Receipts hidden from customer — click to show'}
                className={clsx(
                  'flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium transition-colors border',
                  invoice.include_receipts
                    ? 'bg-indigo-500/10 text-indigo-400 border-indigo-500/30 hover:bg-indigo-500/20'
                    : 'bg-[var(--bg-overlay)] text-[var(--text-muted)] border-[var(--border)] hover:bg-[var(--bg-elevated)]'
                )}
              >
                {invoice.include_receipts ? <Eye size={12} /> : <EyeOff size={12} />}
                Receipts
              </button>
            )}
            <button onClick={onClose} className="p-1 text-[var(--text-muted)] hover:text-[var(--text-primary)] transition-colors">
              <X size={18} />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Meta */}
          <div className="px-6 py-4 border-b border-[var(--border)] bg-[var(--bg-base)]">
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Vehicle</p>
                <p className="font-medium text-[var(--text-primary)]">
                  {invoice.registration} — {invoice.make} {invoice.model}
                </p>
              </div>
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Issue date</p>
                <p className="text-[var(--text-primary)]">{fmtDate(invoice.issue_date)}</p>
              </div>
              <div>
                <p className="text-[var(--text-muted)] text-xs mb-0.5">Due date</p>
                <p className="text-[var(--text-primary)]">{fmtDate(invoice.due_date)}</p>
              </div>
              {invoice.paid_at && (
                <div>
                  <p className="text-[var(--text-muted)] text-xs mb-0.5">Paid</p>
                  <p className="text-green-400">
                    {fmtDate(invoice.paid_at)}
                    {invoice.payment_method ? ` · ${invoice.payment_method}` : ''}
                  </p>
                </div>
              )}
              {invoice.finalised_at && (
                <div>
                  <p className="text-[var(--text-muted)] text-xs mb-0.5">Finalised</p>
                  <p className="text-indigo-400">{fmtDate(invoice.finalised_at)}</p>
                </div>
              )}
            </div>
          </div>

          {/* Line items */}
          <div className="px-6 py-4">
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                Line Items
              </p>
              {canAdmin && editable && invoice.job_id && (
                <button
                  onClick={handlePopulate}
                  disabled={populate.isPending}
                  className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium
                             bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)] border border-[var(--border)]
                             text-[var(--text-secondary)] hover:text-[var(--text-primary)] rounded-lg transition-colors disabled:opacity-50"
                >
                  <RefreshCw size={12} className={populate.isPending ? 'animate-spin' : ''} />
                  {populate.isPending ? 'Populating…' : 'Populate from job'}
                </button>
              )}
            </div>

            {items.length === 0 ? (
              <p className="text-sm text-[var(--text-muted)] italic">No items added yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-[var(--text-muted)] text-xs border-b border-[var(--border)]">
                      <th className="text-left pb-2 font-medium">Description</th>
                      <th className="text-left pb-2 font-medium">Type</th>
                      <th className="text-right pb-2 font-medium">Qty</th>
                      <th className="text-right pb-2 font-medium">Unit</th>
                      <th className="text-right pb-2 font-medium">VAT</th>
                      <th className="text-right pb-2 font-medium">Total</th>
                      {canAdmin && <th className="w-24 pb-2" />}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[var(--border)]">
                    {items.map(item => (
                      <tr key={item.id} className={clsx('text-[var(--text-primary)]', item.already_paid && 'opacity-60')}>
                        <td className="py-2.5 pr-3">
                          <span className={clsx(item.already_paid && 'line-through text-[var(--text-muted)]')}>
                            {item.description}
                          </span>
                          {item.already_paid && (
                            <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-green-500/15 text-green-400 rounded-full">
                              Pre-paid
                            </span>
                          )}
                        </td>
                        <td className="py-2.5 pr-3 capitalize text-[var(--text-secondary)] text-xs">{item.type}</td>
                        <td className="py-2.5 text-right">{Number(item.quantity)}</td>
                        <td className="py-2.5 text-right">{fmtGBP(item.unit_price)}</td>
                        <td className="py-2.5 text-right text-[var(--text-muted)] text-xs">{item.vat_rate}%</td>
                        <td className="py-2.5 text-right font-medium">{fmtGBP(item.total)}</td>
                        {canAdmin && (
                          <td className="py-2.5 pl-2">
                            <div className="flex items-center gap-1 justify-end">
                              {editable && (
                                <button
                                  onClick={() => handleToggleAlreadyPaid(item)}
                                  disabled={updateItem.isPending}
                                  title={item.already_paid ? 'Mark as not pre-paid' : 'Mark as pre-paid by customer'}
                                  className={clsx(
                                    'px-2 py-1 text-xs font-medium rounded transition-colors disabled:opacity-50',
                                    item.already_paid
                                      ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                                      : 'bg-[var(--bg-overlay)] text-[var(--text-muted)] hover:text-green-400 hover:bg-green-500/10'
                                  )}
                                >
                                  {item.already_paid ? 'Pre-paid' : 'Pre-paid?'}
                                </button>
                              )}
                              {editable && (
                                <button
                                  onClick={() => handleDeleteItem(item.id)}
                                  disabled={deleteItem.isPending}
                                  className="p-1 text-[var(--text-muted)] hover:text-red-400 rounded transition-colors disabled:opacity-50"
                                >
                                  <Trash2 size={13} />
                                </button>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {editable && <AddItemForm invoiceId={invoice.id} />}

            {items.length > 0 && (
              <div className="mt-4 pt-4 border-t border-[var(--border)] space-y-1 text-sm">
                <div className="flex justify-between text-[var(--text-secondary)]">
                  <span>Subtotal</span><span>{fmtGBP(invoice.subtotal)}</span>
                </div>
                <div className="flex justify-between text-[var(--text-secondary)]">
                  <span>VAT</span><span>{fmtGBP(invoice.vat_amount)}</span>
                </div>
                {hasPrePaid && (
                  <div className="flex justify-between text-green-400 text-xs pt-1">
                    <span>Pre-paid by customer</span>
                    <span>−{fmtGBP(prePaidTotal)}</span>
                  </div>
                )}
                <div className="flex justify-between font-semibold text-[var(--text-primary)] text-base pt-1">
                  <span>Total due</span><span>{fmtGBP(invoice.total)}</span>
                </div>
              </div>
            )}
          </div>

          {/* Receipts */}
          {(canAdmin || invoice.include_receipts) && (
            <div className="px-6 pb-4">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                  Receipts {receipts.length > 0 && `(${receipts.length})`}
                  {!invoice.include_receipts && canAdmin && (
                    <span className="ml-2 normal-case font-normal text-orange-400">(hidden from customer)</span>
                  )}
                </p>
                {canAdmin && !invoice.is_finalised && (
                  <button
                    onClick={() => fileRef.current?.click()}
                    disabled={addReceipt.isPending}
                    className="flex items-center gap-1.5 text-xs text-indigo-400 hover:text-indigo-300
                               disabled:opacity-50 transition-colors"
                  >
                    <Paperclip size={11} />
                    {addReceipt.isPending ? 'Uploading…' : 'Attach receipt'}
                  </button>
                )}
                <input ref={fileRef} type="file" accept="image/*,.pdf" className="hidden" onChange={handleReceiptUpload} />
              </div>
              {receipts.length === 0 ? (
                <p className="text-xs text-[var(--text-muted)]">No receipts attached.</p>
              ) : (
                <div className="space-y-1.5">
                  {receipts.map(r => (
                    <div key={r.id} className="flex items-center gap-2 group">
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 text-xs text-indigo-400 hover:text-indigo-300 transition-colors flex-1 min-w-0"
                      >
                        <Paperclip size={11} className="shrink-0" />
                        <span className="truncate">{r.filename}</span>
                        <ExternalLink size={10} className="shrink-0" />
                      </a>
                      {r.uploaded_by_name && (
                        <span className="text-xs text-[var(--text-muted)] shrink-0">{r.uploaded_by_name}</span>
                      )}
                      {canAdmin && !invoice.is_finalised && (
                        <button
                          onClick={() => handleDeleteReceipt(r.id)}
                          className="p-0.5 text-[var(--text-muted)] hover:text-red-400 opacity-0 group-hover:opacity-100
                                     transition-all shrink-0"
                        >
                          <X size={12} />
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {invoice.notes && (
            <div className="px-6 pb-4">
              <p className="text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] mb-1">Notes</p>
              <p className="text-sm text-[var(--text-primary)] whitespace-pre-line">{invoice.notes}</p>
            </div>
          )}
        </div>

        {/* Footer actions */}
        {canAdmin && (canSend || canFinalise || canMarkPaid) && (
          <div className="px-6 py-4 border-t border-[var(--border)] shrink-0 flex flex-wrap gap-2">
            {canSend && (
              <button onClick={handleSend} disabled={sendInvoice.isPending || items.length === 0}
                className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                           disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors">
                <Send size={15} />
                {sendInvoice.isPending ? 'Sending…' : 'Send Invoice'}
              </button>
            )}
            {canMarkPaid && (
              <button onClick={() => setShowPaid(true)}
                className="flex items-center gap-2 px-4 py-2.5 bg-green-600 hover:bg-green-700
                           text-white text-sm font-medium rounded-lg transition-colors">
                <CreditCard size={15} />
                Mark as Paid
              </button>
            )}
            {canFinalise && (
              <button onClick={handleFinalise} disabled={finalise.isPending}
                className="flex items-center gap-2 px-4 py-2.5 bg-[var(--bg-overlay)] hover:bg-[var(--bg-elevated)]
                           border border-[var(--border)] text-[var(--text-secondary)] text-sm font-medium rounded-lg transition-colors disabled:opacity-50">
                <Lock size={15} />
                {finalise.isPending ? 'Finalising…' : 'Finalise'}
              </button>
            )}
            {canSend && items.length === 0 && (
              <p className="w-full text-xs text-[var(--text-muted)]">Add at least one item before sending.</p>
            )}
          </div>
        )}
      </div>

      {showPaid && (
        <MarkPaidModal invoice={invoice} onClose={() => setShowPaid(false)} />
      )}
    </div>
  )
}

// ─── Invoice card ─────────────────────────────────────────────────────────────

function InvoiceCard({ invoice, onOpen }) {
  const isOverdue = invoice.status === 'overdue'
  return (
    <button
      onClick={onOpen}
      className={clsx(
        'w-full text-left p-5 bg-[var(--bg-surface)] border rounded-xl transition-colors',
        isOverdue
          ? 'border-red-500/40 hover:border-red-500/70'
          : 'border-[var(--border)] hover:border-[var(--border)]'
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="flex items-center gap-2 mb-1 flex-wrap">
            <p className="font-mono text-sm font-semibold text-[var(--text-primary)]">
              {invoice.invoice_number}
            </p>
            <StatusBadge status={invoice.status} finalised={invoice.is_finalised} />
          </div>
          <p className="text-sm text-[var(--text-secondary)]">
            {invoice.registration} — {invoice.make} {invoice.model}
          </p>
          {invoice.due_date && invoice.status !== 'paid' && (
            <p className={clsx('text-xs mt-1', isOverdue ? 'text-red-400' : 'text-[var(--text-muted)]')}>
              Due {fmtDate(invoice.due_date)}
            </p>
          )}
          {invoice.status === 'paid' && invoice.paid_at && (
            <p className="text-xs text-green-400 mt-1">Paid {fmtDate(invoice.paid_at)}</p>
          )}
        </div>
        <div className="text-right shrink-0">
          <p className="text-lg font-bold text-[var(--text-primary)]">{fmtGBP(invoice.total)}</p>
          <p className="text-xs text-[var(--text-muted)]">{fmtDate(invoice.issue_date)}</p>
        </div>
      </div>
    </button>
  )
}

// ─── Filter tabs ──────────────────────────────────────────────────────────────

const FILTERS = ['all', 'draft', 'sent', 'paid', 'overdue', 'cancelled']

function FilterTabs({ active, onChange }) {
  return (
    <div className="flex gap-1 flex-wrap">
      {FILTERS.map(f => (
        <button key={f} onClick={() => onChange(f)}
          className={clsx(
            'px-3 py-1.5 rounded-lg text-xs font-medium capitalize transition-colors',
            active === f
              ? 'bg-indigo-600 text-white'
              : 'bg-[var(--bg-surface)] border border-[var(--border)] text-[var(--text-secondary)] hover:text-[var(--text-primary)]'
          )}>
          {f === 'all' ? 'All' : STATUS[f]?.label || f}
        </button>
      ))}
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function InvoicesPage() {
  const { user }   = useAuth()
  const canAdmin   = ['garage_admin', 'platform_admin'].includes(user?.role)

  const [filter,     setFilter]     = useState('all')
  const [showCreate, setShowCreate] = useState(false)
  const [detailId,   setDetailId]   = useState(null)

  const queryParams = filter === 'all' ? {} : { status: filter }
  const { data: invoices = [], isLoading } = useInvoices(queryParams)

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">Invoices</h1>
          {!isLoading && invoices.length > 0 && (
            <p className="mt-1 text-sm text-[var(--text-secondary)]">
              {invoices.length} {invoices.length === 1 ? 'invoice' : 'invoices'}
            </p>
          )}
        </div>
        {canAdmin && (
          <button onClick={() => setShowCreate(true)}
            className="flex items-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700
                       text-white text-sm font-medium rounded-lg transition-colors">
            <Plus size={16} />
            New Invoice
          </button>
        )}
      </div>

      <FilterTabs active={filter} onChange={setFilter} />

      {isLoading ? (
        <div className="space-y-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl animate-pulse" />
          ))}
        </div>
      ) : invoices.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <div className="w-16 h-16 rounded-2xl bg-[var(--bg-surface)] border border-[var(--border)] flex items-center justify-center mb-4">
            <Receipt size={28} className="text-[var(--text-muted)]" />
          </div>
          <h3 className="font-display font-bold text-lg text-[var(--text-primary)] mb-2">No invoices</h3>
          <p className="text-sm text-[var(--text-secondary)] max-w-xs">
            {filter === 'all' ? 'Create your first invoice.' : `No ${filter} invoices.`}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {invoices.map(inv => (
            <InvoiceCard key={inv.id} invoice={inv} onOpen={() => setDetailId(inv.id)} />
          ))}
        </div>
      )}

      {showCreate && <CreateInvoiceModal onClose={() => setShowCreate(false)} />}
      {detailId && (
        <InvoiceDetailModal
          invoiceId={detailId}
          canAdmin={canAdmin}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  )
}
